import type { AxiosResponse } from "axios";
import useSWR, { KeyedMutator } from "swr";
import { TagAssignmentRun } from "@/models/tagAssignmentRun";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import {
  ExecuteTagAssignmentRequest,
  ExecuteTagAssignmentResponse,
  GetTagAssignmentRunsResponse,
  TagAssignmentRunSchema,
} from "@/services/tagAssignment/tagAssignment.api.types";
import TagAssignmentEndpoints from "@/services/tagAssignment/tagAssignment.endpoint";

export class TagAssignmentService {
  static async executeTagAssignment(
    workspaceId: string,
    startDate: string,
    endDate: string,
  ): Promise<ExecuteTagAssignmentResponse> {
    const data: ExecuteTagAssignmentRequest = {
      workspaceId,
      startDate,
      endDate,
    };

    const response = await ApiService.fetchData<
      ExecuteTagAssignmentRequest,
      ExecuteTagAssignmentResponse
    >({
      url: TagAssignmentEndpoints.EXECUTE_TAG_ASSIGNMENT,
      data,
      method: "POST",
    });

    return response.data;
  }

  static getTagAssignmentRuns(
    workspaceId: string | null,
    offset: number = 0,
    limit: number = 10,
  ): {
    runs: TagAssignmentRun[] | undefined;
    total: number | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<GetTagAssignmentRunsResponse>>;
  } {
    const url = workspaceId
      ? `${TagAssignmentEndpoints.GET_TAG_ASSIGNMENT_RUNS}?workspace_id=${workspaceId}&offset=${offset}&limit=${limit}`
      : null;

    const fetcher = () =>
      ApiService.fetchData<undefined, GetTagAssignmentRunsResponse>({
        url: url!,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(url, fetcher, defaultSWROption);

    return {
      runs: data?.data.runs.map(buildTagAssignmentRun),
      total: data?.data.total,
      error,
      isLoading,
      mutate,
    };
  }
}

const buildTagAssignmentRun = (run: TagAssignmentRunSchema): TagAssignmentRun =>
  new TagAssignmentRun(
    run.id,
    run.workspaceId,
    new Date(run.startDate),
    new Date(run.endDate),
    run.status,
    new Date(run.createdAt),
    new Date(run.updatedAt),
    run.tagIds,
  );
